<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: GCI 2020
    Author: Dtsmart
    Author URL: http://dtsmart.vn
========================================================================================== -->


<template>
  <div
    class="layout--main"
    :class="[
      layoutTypeClass,
      navbarClasses,
      footerClasses,
      { 'app-page': isAppPage },
    ]"
  >
    <the-customizer
      v-if="!disableCustomizer"
      :footerType="footerType"
      :hideScrollToTop="hideScrollToTop"
      :navbarType="navbarType"
      :navbarColor="navbarColor"
      :routerTransition="routerTransition"
      @toggleHideScrollToTop="toggleHideScrollToTop"
      @updateFooter="updateFooter"
      @updateNavbar="updateNavbar"
      @updateNavbarColor="updateNavbarColor"
      @updateRouterTransition="updateRouterTransition"
    />

    <v-nav-menu
      :navMenuItems="navMenuItems"
      :logo="navMenuLogo"
      :title="sidebarTitle"
      parent=".layout--main"
      :afterLoginConfig="afterLoginConfig"
      :sidebar_key="sidebar_key"
    />

    <div
      id="content-area"
      :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]"
    >
      <div id="content-overlay" />

      <!-- Navbar -->
      <template v-if="mainLayoutType === 'horizontal' && windowWidth >= 1200">
        <the-navbar-horizontal
          :navbarType="navbarType"
          :logo="navMenuLogo"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
        />

        <div style="height: 62px" v-if="navbarType === 'static'"></div>

        <h-nav-menu
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
          :navMenuItems="navMenuItems"
        />
      </template>

      <template v-else>
        <the-navbar-vertical
          :navbarColor="navbarColor"
          :afterLoginConfig="afterLoginConfig"
          :sidebar_key="sidebar_key"
          @OutAfterLogin="outAfterLogin"
          @OutViewNotify="outAfterLogin"
          @OpenSearchAppointmentPopup="openSearchAppointmentPopup"
          @openCallingPopup="openCallingPopup"
          @logout="logout"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
        />
      </template>
      <!-- /Navbar -->

      <div
        class="flex flex-col px-base pt-4 pb-5 receive-calling-block"
        :class="{ 'show-calling': has_calling }"
      >
        <div class="flex mb-4">
          <img
            src="../../assets/images/success-circle-phone-icon.svg"
            class="mr-3"
          />
          <div>
            <p class="text-black font-bold customer-name">
              {{ customerCallingInfo.full_name }}
            </p>
            <p class="text-sm customer-phone">
              {{ StringHelper.phoneNumberFormat(number_calling) }}
            </p>
          </div>
        </div>
        <div class="flex">
          <vs-button
            type="border"
            color="danger"
            class="close-btn"
            v-if="has_calling"
            @click="has_calling = false"
            >Close</vs-button
          >
          <vs-button
            color="primary"
            class="ml-4 receive-calling-block__btn"
            v-if="has_calling"
            @click="openCallingPopup"
            >Open</vs-button
          >
        </div>
      </div>

      <call-info-popup
        :callInfoConfig="callInfoConfig"
        @openBook="openBook"
      ></call-info-popup>

      <appointment-edit-popup
        v-if="popupEditConfig.isActive"
        :config="popupEditConfig"
        @closePopup="closeEditPopup"
        :key="popupEditConfig.key"
        :customer="popupEditConfig.customer"
      />

      <!-- <div class="low-remaining-sms-message" v-if="show_sms_message && smsCount && smsCount.sms_count_active == 1 && smsCount.sms_count < 200"> -->
      <div class="low-remaining-sms-message" v-if="isShowSMS">
        <div class="content">
          Your remaining SMS is running low. Please recharge a new package as soon as possible.
          <feather-icon icon="XIcon" svgClasses="w-6 h-5 stroke-current text-white" class="close-icon" @click.stop="show_sms_message = false" />
        </div>
      </div>
      <div class="low-remaining-sms-message bg-primary" :style="`top: ${isShowSMS ? 19.5 : 0}px`" v-if="userInfo.trialExpireDate && userInfo.trialExpireDate != undefined && show_trial_day">
        <div class="content">
          Your account has {{ userInfo.trialExpireDate + (userInfo.trialExpireDate > 1 ? ' days': ' day') }} left for free trial. Please contact your agent or call 832-968-6668 to upgrade.
          <feather-icon icon="XIcon" svgClasses="w-6 h-5 stroke-current text-white" class="close-icon" @click.stop="show_trial_day = false" />
        </div>
      </div>

      <vs-prompt
        @cancel="cancelAlert"
        @accept="acceptAlert"
        :active.sync="activeTrialPrompt"
        type="confirm"
        title="Notification"
      >
        <div class="con-exemple-prompt">
          Your account has been expired for free trial. Please contact your agent or call 832-968-6668 to upgrade.
        </div>
      </vs-prompt>

      <div class="content-wrapper">
        <div class="router-view" id="my-router-view" v-if="showContent && userInfo && userInfo.trialExpireDate != 0">
          <div class="router-content" id="my-router-content">
            <transition :name="routerTransition">
              <div
                v-if="$route.meta.breadcrumb || $route.meta.pageTitle"
                class="router-header flex flex-wrap items-center mb-6"
              >
                <div
                  class="content-area__heading"
                  :class="{
                    'pr-4 border-0 md:border-r border-solid border-grey-light':
                      $route.meta.breadcrumb,
                  }"
                >
                  <h2 class="mb-1">{{ routeTitle }}</h2>
                </div>

                <!-- BREADCRUMB -->
                <vx-breadcrumb
                  class="ml-4 md:block hidden"
                  v-if="$route.meta.breadcrumb"
                  :route="$route"
                />

                <!-- DROPDOWN -->
                <vs-dropdown
                  vs-trigger-click
                  class="ml-auto md:block hidden cursor-pointer"
                >
                  <vs-button radius icon="icon-settings" icon-pack="feather" />

                  <vs-dropdown-menu class="w-32">
                    <vs-dropdown-item>
                      <div
                        @click="$router.push('/pages/profile').catch(() => {})"
                        class="flex items-center"
                      >
                        <feather-icon
                          icon="UserIcon"
                          class="inline-block mr-2"
                          svgClasses="w-4 h-4"
                        />
                        <span>Profile</span>
                      </div>
                    </vs-dropdown-item>
                    <vs-dropdown-item>
                      <div
                        @click="$router.push('/apps/todo').catch(() => {})"
                        class="flex items-center"
                      >
                        <feather-icon
                          icon="CheckSquareIcon"
                          class="inline-block mr-2"
                          svgClasses="w-4 h-4"
                        />
                        <span>Tasks</span>
                      </div>
                    </vs-dropdown-item>
                    <vs-dropdown-item>
                      <div
                        @click="$router.push('/apps/email').catch(() => {})"
                        class="flex items-center"
                      >
                        <feather-icon
                          icon="MailIcon"
                          class="inline-block mr-2"
                          svgClasses="w-4 h-4"
                        />
                        <span>Inbox</span>
                      </div>
                    </vs-dropdown-item>
                  </vs-dropdown-menu>
                </vs-dropdown>
              </div>
            </transition>
            <div class="content-area__content">
              <back-to-top
                bottom="5%"
                visibleoffset="500"
                v-if="!hideScrollToTop"
              >
                <vs-button
                  icon-pack="feather"
                  icon="icon-arrow-up"
                  class="shadow-lg btn-back-to-top"
                />
              </back-to-top>

              <transition :name="routerTransition" mode="out-in">
                <router-view
                  v-if="userInfo.name"
                  @changeRouteTitle="changeRouteTitle"
                  :dataChange="dataChange"
                  @SelectItem="selectItem"
                  @InAfterLogin="inAfterLogin"
                  @OutAfterLogin="outAfterLogin"
                  :searchAppointmentConfig="searchAppointmentConfig"
                ></router-view>
                <div v-else>
                  <div class="my-error-page" v-if="isLoaded">
                    <div class="flex flex-col align-center">
                      <img src="../../assets/images/risk-icon.png" />
                      <p class="text-primary font-bold" style="font-size: 40px">
                        Opp, something went wrong!!
                      </p>
                      <!-- <p class="text-xl mt-6">Please contact us for more info at <b>832-968-6668</b> or try to logout and login again!</p> -->
                      <p class="text-xl mt-6" v-html="error_message"></p>
                      <vs-button class="w-1/2 mt-base text-2xl" @click="logout"
                        >LOGOUT</vs-button
                      >
                    </div>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
      <the-footer />

      <NoLongerUpdateAlert />
    </div>
  </div>
</template>


<script>
/* eslint-disable */
import BackToTop from "vue-backtotop";
import HNavMenu from "@/layouts/components/horizontal-nav-menu/HorizontalNavMenu.vue";
import navMenuItemsBase from "@/layouts/components/vertical-nav-menu/navMenuItems.js";
import TheCustomizer from "@/layouts/components/customizer/TheCustomizer.vue";
import TheNavbarHorizontal from "@/layouts/components/navbar/TheNavbarHorizontal.vue";
import TheNavbarVertical from "@/layouts/components/navbar/TheNavbarVertical.vue";
import TheFooter from "@/layouts/components/TheFooter.vue";
import themeConfig from "@/../themeConfig.js";
import VNavMenu from "@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";
import NoLongerUpdateAlert from "@/layouts/components/NoLongerUpdateAlert.vue";
import { StringHelper } from "../../models/string_helper";
// import { ApolloClient } from "apollo-client";
// import { createHttpLink } from "apollo-link-http";
// import { InMemoryCache } from "apollo-cache-inmemory";
// import VueApollo from "vue-apollo";
// import gql from "graphql-tag";
// import { split } from "apollo-link";
// import { WebSocketLink } from "apollo-link-ws";
// import { getMainDefinition } from "apollo-utilities";
import AppointmentEditPopup from "@/views/appointment/AppointmentEditPopup.vue";
import moment from "moment";
import mqtt from "mqtt";

// import { EventSourcePolyfill } from "event-source-polyfill";
// import { domain } from "../../axios";
// import { topicList } from "../../store/topicList";
// import firebase from 'firebase'
// import Vue from 'vue'
// import LoadScript from 'vue-plugin-load-script';

// Vue.use(LoadScript);

// Vue.loadScript('https://www.gstatic.com/firebasejs/3.5.2/firebase-app.js')
// Vue.loadScript('https://www.gstatic.com/firebasejs/3.5.2/firebase-messaging.js')
// import Vue from 'vue'
// import messaging from '../../static/firebase-messaging-sw'

// HTTP connection to the API
// const httpLink = createHttpLink({
//   // You should use an absolute URL here
//   uri: "https://dev.api.fastboy.dev/messaging/graphql",
//   headers: {
//     Authorization:
//       "Bearer eyJhbGciOiJSUzI1NiJ9.eyJpc3MiOiJodHRwczpcL1wvZGV2LmFwaS5mYXN0Ym95LmRldlwvbWVzc2FnaW5nIiwiYXVkIjoiaGFzdXJhIiwiaWF0IjoxNjMwMDQ5OTUxLCJleHAiOjE2MzI2NDE5NTEsImhhc3VyYSI6eyJhcGktbWVzc2FnaW5nIjp7IngtaGFzdXJhLWFsbG93ZWQtcm9sZXMiOlsiUk9MRV9UUkFDS0VSIl0sIngtaGFzdXJhLWRlZmF1bHQtcm9sZSI6IlJPTEVfVFJBQ0tFUiIsIngtaGFzdXJhLXBob25lbnVtYmVyIjoiKzE5NzkyMTcyMDEyIn19fQ.g4jScDQySo0Wr44m9vuIw3D_EHUixIhEsVw3_o25ZU6l43WLNXcpJ61aMvM4QiI_HQSQTHtTSG17PYh5iaZnvCkKqxKTI_ensdBN0jJDh1pQ4_3pR0dWqCnDPspDXSS9-IdAMwzoYMLDWPg3ste-oxiqHin4mwmUINLjks5_Iv0FT5Nz-AMZ_OIgupscoLvRgOs_tbmKTL8rM4pddZfvJA1VnomrJ5hzq6mmrDP2RKZU35DP4-lQQQRQh5XnYKrfkvBWm-yIc1sZxEV0QRJ99y82zaNBcAhjJDIgcehGXsw4Vs41kJnYgPhBaiRv5lNZ_KDTkFb24sTUSNfz6_3EKC_vLlPtZzjwXb7hRruebo_PhCINZO7turced48sLyKlcXxlIc0j1J9JrWKcf3YtiYgEsByQ2HoXvj4pd_xF4H-2wc23sOLJ131Wtn4TghO0uXfeYiXkwXSxKdz6Tpuxd0yuXG_KYtFy3MS26a2FdOGmYshn08KT7K0Mz3820BMgs8GA_wMSpay-bySoVST3CMF_pQ6GWCns8IzCAUn7H8e092OYbBP33THtLAjmxvRJq66QPLoaQqD8vq8LavsFs-64euGWsVEDuutVx1qlQKzZOkdc6XBIuClFrA2iRFBNVUVvIpVHxGfl0HKWiKNMU6CmoWGJEsWYfMFouo3Wits",
//   },
// });

// const wsLink = new WebSocketLink({
//   uri: "wss://dev.api.fastboy.dev/messaging/graphql",
//   options: {
//     reconnect: true,
//     connectionParams: {
//       headers: {
//         Authorization:
//           "Bearer eyJhbGciOiJSUzI1NiJ9.eyJpc3MiOiJodHRwczpcL1wvZGV2LmFwaS5mYXN0Ym95LmRldlwvbWVzc2FnaW5nIiwiYXVkIjoiaGFzdXJhIiwiaWF0IjoxNjMwMDQ5OTUxLCJleHAiOjE2MzI2NDE5NTEsImhhc3VyYSI6eyJhcGktbWVzc2FnaW5nIjp7IngtaGFzdXJhLWFsbG93ZWQtcm9sZXMiOlsiUk9MRV9UUkFDS0VSIl0sIngtaGFzdXJhLWRlZmF1bHQtcm9sZSI6IlJPTEVfVFJBQ0tFUiIsIngtaGFzdXJhLXBob25lbnVtYmVyIjoiKzE5NzkyMTcyMDEyIn19fQ.g4jScDQySo0Wr44m9vuIw3D_EHUixIhEsVw3_o25ZU6l43WLNXcpJ61aMvM4QiI_HQSQTHtTSG17PYh5iaZnvCkKqxKTI_ensdBN0jJDh1pQ4_3pR0dWqCnDPspDXSS9-IdAMwzoYMLDWPg3ste-oxiqHin4mwmUINLjks5_Iv0FT5Nz-AMZ_OIgupscoLvRgOs_tbmKTL8rM4pddZfvJA1VnomrJ5hzq6mmrDP2RKZU35DP4-lQQQRQh5XnYKrfkvBWm-yIc1sZxEV0QRJ99y82zaNBcAhjJDIgcehGXsw4Vs41kJnYgPhBaiRv5lNZ_KDTkFb24sTUSNfz6_3EKC_vLlPtZzjwXb7hRruebo_PhCINZO7turced48sLyKlcXxlIc0j1J9JrWKcf3YtiYgEsByQ2HoXvj4pd_xF4H-2wc23sOLJ131Wtn4TghO0uXfeYiXkwXSxKdz6Tpuxd0yuXG_KYtFy3MS26a2FdOGmYshn08KT7K0Mz3820BMgs8GA_wMSpay-bySoVST3CMF_pQ6GWCns8IzCAUn7H8e092OYbBP33THtLAjmxvRJq66QPLoaQqD8vq8LavsFs-64euGWsVEDuutVx1qlQKzZOkdc6XBIuClFrA2iRFBNVUVvIpVHxGfl0HKWiKNMU6CmoWGJEsWYfMFouo3Wits",
//       },
//     },
//   },
// });

// const link = split(
//   // split based on operation type
//   ({ query }) => {
//     const definition = getMainDefinition(query);
//     return (
//       definition.kind === "OperationDefinition" &&
//       definition.operation === "subscription"
//     );
//   },
//   wsLink,
//   httpLink
// );

// // Cache implementation
// const cache = new InMemoryCache();

// // Create the apollo client
// const apolloClient = new ApolloClient({
//   link: link,
//   cache,
//   // connectToDevTools: true,
// });
// const apolloProvider = new VueApollo({
//   defaultClient: apolloClient,
//   watchLoading(state, mod) {
//     console.log("Global loading", mod);
//   },
//   errorHandler(error) {
//     console.log("Global error handler");
//     console.error(error);
//   },
// });
// const SUBSCRIBE_QUERY = gql`
//   subscription test {
//     plivo_voice_tracking(limit: 1, order_by: { created_at: desc }) {
//       id
//       from_number
//       created_at
//       status
//     }
//   }
// `;
export default {
  // apolloProvider,
  components: {
    BackToTop,
    HNavMenu,
    TheCustomizer,
    TheFooter,
    TheNavbarHorizontal,
    TheNavbarVertical,
    VNavMenu,
    AppointmentEditPopup,
    NoLongerUpdateAlert
  },
  props: {
    showContent: {
      type: Boolean,
      default: true,
    },
  },
  // apollo: {
  //   $subscribe: {
  //     test: {
  //       query: SUBSCRIBE_QUERY,
  //       operation: "subscription",
  //       result({ data }) {
  //         if (data.plivo_voice_tracking && data.plivo_voice_tracking[0]) {
  //           // console.log(data.plivo_voice_tracking[0].status);
  //           if (
  //             data.plivo_voice_tracking[0].status == "ready" ||
  //             data.plivo_voice_tracking[0].status == "in-progress"
  //           ) {
  //             this.has_calling = true;
  //             this.number_calling = data.plivo_voice_tracking[0].from_number;
  //             let params = {
  //               phone: this.number_calling,
  //             };
  //             this.$store.dispatch(
  //               "customerList/GET_CUSTOMER_CALLING_INFO",
  //               params
  //             );
  //           } else {
  //             this.has_calling = false;
  //           }
  //         } else {
  //           this.has_calling = false;
  //         }
  //       },
  //     },
  //   },
  //   // test: {
  //   //   query: gql`
  //   //     query MyQuery {
  //   //       plivo_voice_tracking(limit: 1, order_by: { created_at: desc }) {
  //   //         id
  //   //         from_number
  //   //         created_at
  //   //         status
  //   //       }
  //   //     }
  //   //   `,
  //   //   operation: "subscription",
  //   //   subscribeToMore: {
  //   //     document: SUBSCRIBE_QUERY,
  //   //     updateQuery: (previousResult, { subscriptionData }) => {
  //   //       console.log("hihi");
  //   //       console.log(subscriptionData);
  //   //     },
  //   //   },
  //   // },
  // },
  data() {
    return {
      StringHelper,
      disableCustomizer: themeConfig.disableCustomizer,
      disableThemeTour: themeConfig.disableThemeTour,
      footerType: themeConfig.footerType || "static",
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || "#fff",
      navbarType: themeConfig.navbarType || "floating",
      navMenuLogo: require("@/assets/images/logo/newlogo.png"),
      routerTransition: themeConfig.routerTransition || "none",
      routeTitle: this.$route.meta.pageTitle,
      dataChange: { message: "", key: 0 },
      afterLoginConfig: {
        isIn: false,
        type: "",
      },
      itemType: "",
      sidebar_key: 0,
      isLoaded: undefined,
      key: this.$route.query.key ? parseInt(this.$route.query.key) + 1 : 0,
      sidebarTitle: "",
      isOpenLoading: false,
      myWindowWidth: 0,
      searchAppointmentConfig: {
        isActive: false,
      },
      trial_expired: false,
      error_message: "Please try to logout and login again!",
      has_calling: false,
      call_session_id: "",
      callInfoConfig: {
        isOpen: false,
      },
      number_calling: null,
      isIncoming: true,
      popupEditConfig: {
        isActive: false,
        isNewEvent: true,
        appointmentId: null,
        initDate: null,
        key: 0,
        staff_id: null,
        customer: null,
      },
      connection: {
        host: "dev-broker.dtsmart.dev",
        port: 443,
        endpoint: "/mqtt",
        clean: true, // Reserved session
        connectTimeout: 4000, // Time out
        reconnectPeriod: 4000, // Reconnection interval
        // Certification Information
        clientId: "vuejs_app_" + Math.random(),
        username: "admin",
        password: "123456abcA",
      },
      client: {
        connected: false,
      },
      subscription: {
        topic: "/gci/{id}/call/incoming",
        qos: 0,
      },
      subscribeSuccess: false,
      element: null,
      show_sms_message: true,
      show_trial_day: true,
      activeTrialPrompt: false
    };
  },
  watch: {
    // dataChange:{
    //   immediate: true,
    //   deep: true,
    //   handler: function () {
    //     if(this.dataChange) {
    //       console.log('a')
    //       this.$vs.notify({
    //         title:'Notification',
    //         text:'You have a new notification!',
    //         color:'warning',
    //         position:'top-right'
    //       });
    //       if (!("Notification" in window)) {
    //         alert("This browser does not support desktop notification");
    //         return;
    //       }
    //       else if (Notification.permission === "granted") {
    //         // If it's okay let's create a notification
    //         // eslint-disable-next-line
    //         var notification = new Notification("You have a new notification!");
    //         return;
    //       }

    //       // Otherwise, we need to ask the user for permission
    //       else if (Notification.permission !== "denied") {
    //         Notification.requestPermission().then(function (permission) {
    //             if (permission === "granted") {
    //               // eslint-disable-next-line
    //               var notification = new Notification("You have a new notification!");
    //               return ;
    //             }
    //           });
    //         }
    //       }
    //   }
    // },
    $route() {
      this.routeTitle = this.$route.meta.pageTitle;
      this.getType(this.$route.name);
      this.$store.dispatch("smsCount/GET_SMS_COUNT");
    },
    isThemeDark(val) {
      const color = this.navbarColor == "#fff" && val ? "#10163a" : "#fff";
      this.updateNavbarColor(color);
    },
    "$store.state.mainLayoutType"(val) {
      this.setNavMenuVisibility(val);
      this.disableThemeTour = true;
    },
    windowWidth(val) {
      if (val < 1200) this.disableThemeTour = true;
    },
    verticalNavMenuWidth() {
      this.disableThemeTour = true;
    },
    isLoaded: {
      immediate: true,
      handler() {
        if (this.isLoaded == undefined) {
          this.openLoadingInDiv();
        } else {
          this.closeLoading();
        }
      },
    },
    config(data) {
      if (
        typeof data.mqtt !== "undefined" &&
        data.mqtt != null &&
        this.subscribeSuccess == false &&
        typeof data.mqtt.config !== "undefined" &&
        data.mqtt.config.call_feature == 1
      ) {
        this.subscription = {
          topic: `/gci/${data.mqtt.id}/call/incoming`,
          qos: 0,
        };
        this.doSubscribe();
      }
    },
    dashboardRedirectUrl: {
      immediate: true,
      deep: true,
      handler() {
        if(this.dashboardRedirectUrl) {
          window.location.href = this.dashboardRedirectUrl;
        }
      }
    }
  },
  computed: {
    userInfo() {
      return this.$store.state.auth.userInfo;
    },

    currentToken() {
      return this.$store.state.auth.token;
    },

    config() {
      return this.$store.getters["auth/config"];
    },

    isShowSMS() {
      return this.show_sms_message && this.smsCount && this.smsCount.sms_count_active == 1 && this.smsCount.sms_count < 200;
    },

    dashboardRedirectUrl() {
      if(!this.config || !this.config.dashboard_redirect_url) return "";
      let result = this.config.dashboard_redirect_url.replace("__TOKEN__", this.currentToken);
      if(result) {
        result = result.replace("__ID__", localStorage.getItem("domainId"));
      }
      return result || "";
    },

    smsCount() {
      return this.$store.getters["smsCount/smsCount"];
    },

    bodyOverlay() {
      return this.$store.state.bodyOverlay;
    },
    contentAreaClass() {
      if (this.mainLayoutType === "vertical") {
        if (
          this.verticalNavMenuWidth == "default" &&
          !this.afterLoginConfig.isIn
        )
          if (this.myWindowWidth < 1200) {
            // Ê nếu có sai thì bỏ if else tại đây nha, chỉ để lại return "content-area-reduced"; thôi ha
            return "no-nav-menu";
          } else {
            return "content-area-reduced";
          }
        else if (this.verticalNavMenuWidth == "reduced")
          if (!this.afterLoginConfig.isIn) {
            return "content-area-lg";
          } else {
            return "";
          }
      }
      // else if(this.mainLayoutType === "boxed") return "content-area-reduced"
      else return "content-area-full";
    },
    footerClasses() {
      return {
        "footer-hidden": this.footerType == "hidden",
        "footer-sticky": this.footerType == "sticky",
        "footer-static": this.footerType == "static",
      };
    },
    isAppPage() {
      return this.$route.path.includes("/apps/") ? true : false;
    },
    isThemeDark() {
      return this.$store.state.theme == "dark";
    },
    layoutTypeClass() {
      return `main-${this.mainLayoutType}`;
    },
    mainLayoutType() {
      return this.$store.state.mainLayoutType;
    },
    navbarClasses() {
      return {
        "navbar-hidden": this.navbarType == "hidden",
        "navbar-sticky": this.navbarType == "sticky",
        "navbar-static": this.navbarType == "static",
        "navbar-floating": this.navbarType == "floating",
      };
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    navMenuItems() {
      let temp = [];
      for (let i = 0; i < navMenuItemsBase.length; i++) {
        if (navMenuItemsBase[i].type.includes(this.itemType)) {
          // if (navMenuItemsBase[i].type == "admin") {
          //   if (
          //     this.config.appointment_feature == 1 &&
          //     navMenuItemsBase[i].admin_type.includes("appointment")
          //   ) {
          //     temp.push(navMenuItemsBase[i]);
          //     continue;
          //   }
          //   if (
          //     this.config.gocheckin_feature == 1 &&
          //     navMenuItemsBase[i].admin_type.includes("gocheckin")
          //   ) {
          //     temp.push(navMenuItemsBase[i]);
          //     continue;
          //   }
          if (navMenuItemsBase[i].admin_type !== undefined) {
            if (
              this.config.order_feature != 1 &&
              navMenuItemsBase[i].admin_type.includes("order")
            ) {
              continue;
            }
            if (
              this.config.pos_feature != 1 &&
              navMenuItemsBase[i].admin_type.includes("gopos")
            ) {
              let navItem = JSON.parse(JSON.stringify(navMenuItemsBase[i]));
              if (navItem.submenu && navItem.submenu.length > 0) {
                for (let j = 0; j < navItem.submenu.length; ) {
                  let item = navItem.submenu[j];
                  if (item.admin_type && item.admin_type == "gopos") {
                    navItem.submenu.splice(j, 1);
                  } else {
                    j++;
                  }
                }
                temp.push(navItem);
                continue;
              } else {
                continue;
              }
            }
          }
          if(
            this.config.stripe_setup_payment_active != 1 &&
            navMenuItemsBase[i].slug &&
            navMenuItemsBase[i].slug.includes("appointment-deposit")
          ) {
            continue;
          }
            // } else {
          //   temp.push(navMenuItemsBase[i]);
          // }
          temp.push(navMenuItemsBase[i]);
        }
      }

      //Disable Calendar Icon on Go Booking
      // if (this.config.appointment_feature == 0) {
      //   for (let i = 0; i < temp.length; i++) {
      //     if (temp[i].slug == "appointment" || temp[i].url == "/appointment") {
      //       temp[i].isDisabled = true;
      //       break;
      //     }
      //   }
      // }
      return temp;
    },
    customerCallingInfo() {
      return this.$store.getters["customerList/customerCallingInfo"];
    },
  },
  methods: {
    openLoadingInDiv() {
      this.$vs.loading({
        container: "#app",
        scale: 0.8,
      });
      if (this.loadTime) {
        clearTimeout(this.loadTime);
        this.loadTime = null;
      }
      this.isOpenLoading = true;
      this.loadTime = setTimeout(() => {
        if (this.isOpenLoading) {
          this.closeLoading();
          // this.$vs.notify({
          //   title:'Notification',
          //   text: this.action_type + ' staff failed',
          //   color:'danger',
          //   position:'top-right'})
          this.$vs.dialog({
            color: "danger",
            title: "Notification",
            text: "Get data failed",
            acceptText: "OK",
          });
        }
      }, 10000);
      // setTimeout( ()=> {
      //   this.$vs.loading.close('#staff-list > .con-vs-loading')
      // }, 500);
    },
    closeLoading() {
      this.isLoaded = true;
      this.$vs.loading.close("#app > .con-vs-loading");
      this.isOpenLoading = false;
    },
    getType(name) {
      if (name == "checkout" || name == "history") {
        this.itemType = "checkout";
      } else if (
        name == "appointment" ||
        name == "blocktime" ||
        name == "bookingstaff" ||
        name == "go-booking-settings" || name == 'appointment-deposit'
      ) {
        this.itemType = "appointment";
      } else if (name == "order" || name == "invoice") {
        this.itemType = "order";
      } else if (name == "article") {
        this.itemType = "article";
      } else if (
        name == "timekeeping" ||
        name == "go-pos-dashboard" ||
        name == "payroll" ||
        name == "reseller" ||
        name == "batch-history" ||
        name == "go-pos-order-detail" ||
        name == "go-pos-ticket-template" ||
        name == "staff_rating" ||
        name == "giftcard" ||
        name == "income" ||
        name == "staff-daily-income"
      ) {
        this.itemType = "gopos";
      } else {
        this.itemType = "admin";
      }
      this.getSidebarTitle();
      this.sidebar_key++;
    },
    getSidebarTitle() {
      if (this.itemType == "checkout") {
        this.sidebarTitle = "GoCheckout";
      } else if (this.itemType == "appointment") {
        this.sidebarTitle = "GoBooking";
      } else if (this.itemType == "order") {
        this.sidebarTitle = "GoOrder";
      } else if (this.itemType == "article") {
        this.sidebarTitle = "Article";
      } else if (this.itemType == "gopos") {
        this.sidebarTitle = "GoPOS";
      } else {
        this.sidebarTitle = "GoCheckIn";
      }
    },
    inAfterLogin() {
      this.afterLoginConfig.isIn = true;
      this.afterLoginConfig.type = "";
      this.sidebar_key++;
    },
    outAfterLogin(type) {
      this.afterLoginConfig.isIn = false;
      this.afterLoginConfig.type = type;
      this.sidebar_key++;
    },
    selectItem(type) {
      this.itemType = type;
      if (this.itemType == "checkout") {
        this.$router.push("/checkout");
        // window.open("/checkout", "_blank");
      } else if (this.itemType == "appointment") {
        this.$router.push("/appointment");
        // window.open("/appointment", "_blank");
      } else if (this.itemType == "order") {
        this.$router.push("/online-order/order");
        // window.open("/online-order/order", "_blank");
      } else if (this.itemType == "article") {
        this.$router.push("/article");
        // window.open("/article", "_blank");
      } else if (this.itemType == "gopos") {
        this.$router.push("/go-pos/income");
        // window.open("/go-pos/income", "_blank");
      } else {
        this.$router.push("/");
        // window.open("/", "_blank");
      }
    },
    changeRouteTitle(title) {
      this.routeTitle = title;
    },
    openSearchAppointmentPopup() {
      this.searchAppointmentConfig.isActive = true;
    },
    updateNavbar(val) {
      if (val == "static") this.updateNavbarColor("#fff");
      this.navbarType = val;
    },
    updateNavbarColor(val) {
      this.navbarColor = val;
      if (val == "#fff") this.isNavbarDark = false;
      else this.isNavbarDark = true;
    },
    updateFooter(val) {
      this.footerType = val;
    },
    updateRouterTransition(val) {
      this.routerTransition = val;
    },
    setNavMenuVisibility(layoutType) {
      if (
        (layoutType === "horizontal" && this.windowWidth >= 1200) ||
        (layoutType === "vertical" && this.windowWidth < 1200)
      ) {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", false);
        this.$store.dispatch("updateVerticalNavMenuWidth", "no-nav-menu");
      } else {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", true);
      }
    },
    toggleHideScrollToTop(val) {
      this.hideScrollToTop = val;
    },
    logout() {
      // This is just for demo Purpose. If user clicks on logout -> redirect
      // this.$router.push('/pages/login').catch(() => {})
      this.$store.dispatch("auth/logout").then(() => {
        this.doUnSubscribe();
        window.location.replace(history.go(-(history.length - 1)));
        window.location.pathname = "/pages/login";
        // this.$router.push("/pages/login");
      });
    },
    // getDomain() {
    //   // console.log(topic);
    //   // if (topic.includes("appointment")) {
    //   //   return domainAppointment.substring(
    //   //     domainAppointment.lastIndexOf("/") + 1
    //   //   );
    //   // }
    //   let temp = domain;
    //   if (domain[domain.length - 1] == "/") {
    //     temp = domain.slice(0, -1);
    //   }
    //   return temp.substring(temp.lastIndexOf("/") + 1);
    // },
    // subscribeTopic() {
    //   let jwt =
    //     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJtZXJjdXJlIjp7InN1YnNjcmliZSI6WyIqIl0sInB1Ymxpc2giOlsiKiJdfX0.teYDSSXh0fwZwLbGN2ECCdk6jYtL6Pi_M5nP-HK7EPA";
    //   const option = {
    //     headers: {
    //       Authorization: "Bearer " + jwt
    //     }
    //   };
    //   const u = new URL("https://s1-hub.newzen.cloud/.well-known/mercure");
    //   topicList.forEach(topic =>
    //     u.searchParams.append("topic", this.getDomain() + topic)
    //   );
    //   this.eventSource = new EventSourcePolyfill(u, option);
    //   this.eventSource.onmessage = e => {
    //     this.dataChange.message = JSON.parse(e.data);
    //     this.dataChange.key++;
    //     // console.log(this.dataChange)
    //   };
    // }
    onResize() {
      this.myWindowWidth = window.innerWidth;
    },
    closeEditPopup(data) {
      this.popupEditConfig.isActive = false;
    },
    openBook(customer) {
      this.popupEditConfig.customer = customer;
      this.popupEditConfig.key = this.popupEditConfig.key + 1;
      this.popupEditConfig.isActive = true;
      this.popupEditConfig.appointmentId = null;
      this.popupEditConfig.isEdit = false;
      this.$store.dispatch("appointment/GET_APPOINTMENT_SPECIFIC_DATE", {
        date: moment().format("YYYY-MM-DD"),
      });
    },
    openCallingPopup() {
      this.callInfoConfig.isOpen = true;
      this.$store.dispatch("service/GET_SERVICE_LIST");
      this.$store.dispatch("staff/GET_STAFF_LIST");
      this.$store.dispatch("customer/GET_CUSTOMER_LIST");
      this.$store.dispatch("business/GET_WORKING_HOUR", {
        business_id: 1,
      });
    },
    createConnection() {
      // const { host, port, endpoint, ...options } = this.connection;
      // const connectUrl = `wss://${host}:${port}${endpoint}`;
      // try {
      //   this.client = mqtt.connect(connectUrl, options);
      // } catch (error) {
      //   console.log("mqtt.connect error", error);
      // }
      // this.client.on("connect", () => {
      this.$mqttClient.on("connect", () => {
        console.log("Connection succeeded!");
      });
      // this.client.on("error", (error) => {
      this.$mqttClient.on("error", (error) => {
        console.log("Connection failed", error);
      });
      // this.client.on("message", (topic, message) => {
      this.$mqttClient.on("message", (topic, message) => {
        // console.log(`Received message ${message} from topic ${topic}`);
        if(topic == this.subscription.topic) {
          let callData = JSON.parse(message);
          //Need to parse to model
          console.log(callData.data);
          console.log(callData.data.event_type);
          console.log(callData.data.payload.from);
          if (
            (callData.data.event_type == "call.initiated" ||
              callData.data.event_type == "call.answered") &&
            this.call_session_id == ""
          ) {
            this.has_calling = true;
            this.call_session_id = callData.data.payload.call_session_id;
            console.log("set session id to: " + this.call_session_id);
            if (callData.data.event_type == "call.initiated") {
              if (callData.data.payload.direction == "incoming") {
                this.isIncoming = true;
              } else {
                this.isIncoming = false;
              }
            }
          } else if (
            callData.data.event_type == "call.hangup" &&
            this.call_session_id == callData.data.payload.call_session_id
          ) {
            this.has_calling = false;
            this.call_session_id = "";
            console.log("set session id to: " + this.call_session_id);
          }
          if (this.isIncoming) {
            this.number_calling = callData.data.payload.from;
          } else {
            this.number_calling = callData.data.payload.to;
          }
          let params = {
            phone: this.number_calling,
          };
          this.$store.dispatch("customerList/GET_CUSTOMER_CALLING_INFO", params);
        }
      });
    },

    doSubscribe() {
      const { topic, qos } = this.subscription;
      // this.client.subscribe(topic, { qos }, (error, res) => {
      this.$mqttClient.subscribe(topic, { qos }, (error, res) => {
        if (error) {
          console.log("Subscribe to topics error", error);
          return;
        }
        this.subscribeSuccess = true;
        console.log("Subscribe to topics res", res);
      });
    },

    doUnSubscribe() {
      const { topic } = this.subscription;
      // this.client.unsubscribe(topic, (error) => {
      this.$mqttClient.unsubscribe(topic, (error) => {
        this.subscribeSuccess = false;
        if (error) {
          console.log("Unsubscribe error", error);
        }
      });
    },

    handleMessage(message) {
      let direction = message.data.data.scroll_delta_dy > 0 ? -1 : 1;
      let distance = 40;
      if (this.getScrollDiv(this.element)) {
        let classList = this.getScrollDiv(this.element).classList;
        let queryString = "";
        for (let i = 0; i < classList.length; i++) {
          queryString += "." + classList[i];
        }
        if (queryString != "") {
          let elems = document.querySelectorAll(queryString);
          if (elems && elems[0]) {
            elems.forEach((elem) => {
              elem.scrollTop += direction * distance;
            });
          }
        }
      }
    },
    getScrollDiv(el) {
      let classList = [
        "overflow-auto",
        "vs-tabs--content",
        "vs-dropdown--menu",
        // "mini-scroll"
      ];
      let styleList = ["overflow", "overflowY"];
      while (el && el.parentNode) {
        // if((el.classList && el.classList.contains('overflow-auto')) || el.style.overflow == "auto" || el.style.overflow == "scroll" || el.style.overflowY == "auto" || el.style.overflowY == "scroll") {
        //   return el;
        // }
        if (
          styleList.find(
            (style) => el.style[style] == "auto" || el.style[style] == "scroll"
          ) ||
          classList.find((className) => el.classList.contains(className))
        ) {
          return el;
        }
        el = el.parentNode;
      }
      return null;
    },
    acceptAlert() {
      this.logout();
    },
    cancelAlert() {
      this.logout();
    }
  },
  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });

    document
      .getElementsByTagName("body")[0]
      .addEventListener("mousemove", (e) => {
        this.element = document.elementFromPoint(e.x, e.y);
        // this.mousePos = { x: e.x, y: e.y };
      });

    if (typeof window.chrome.webview !== "undefined") {
      window.chrome.webview.removeEventListener("message");
      window.chrome.webview.addEventListener("message", this.handleMessage);
    }
  },
  created() {
    // this.subscribeTopic();
    this.$store.dispatch("smsCount/GET_SMS_COUNT");
    const color =
      this.navbarColor == "#fff" && this.isThemeDark
        ? "#10163a"
        : this.navbarColor;
    this.updateNavbarColor(color);
    this.setNavMenuVisibility(this.$store.state.mainLayoutType);
    this.$store
      .dispatch("auth/GET_USER_INFO")
      .then((response, reject) => {
        this.isLoaded = true;
        if(response && response.trialExpireDate == 0) {
          this.activeTrialPrompt = true;
        }
        if (reject == "Version mismatch") {
          this.$router.push("/pages/login");
        }
      })
      .catch((err) => {
        this.isLoaded = false;
        if (err.status == 403) {
          this.trial_expired = true;
          this.error_message = err.message;
        }
      });
    if (
      typeof firebaseMessaging !== "undefined" &&
      firebaseMessaging !== undefined
    ) {
      firebaseMessaging.onMessage((payload) => {
        // console.log(payload);
        if (payload.notification.title !== undefined) {
          this.$vs.notify({
            title: payload.notification.title,
            text: payload.notification.body,
            color: "success",
            position: "top-center",
            time: 4000,
            click: () => {
              if (payload.notification.click_action != "/appointment") {
                this.$router.push({
                  path: payload.notification.click_action,
                });
              } else {
                this.$router.push({
                  name: "appointment",
                  params: { id: parseInt(payload.data.appointment_id) },
                  query: { key: this.key++ },
                });
              }
            },
          });
        }
        // let noti = new Notification("test na", {
        //   data: { url: "/online-order/order" }
        // });
        // noti.onclick = () => {};
      });
    }

    this.getType(this.$route.name);

    this.$store.dispatch("customerList/GET_SMS_TEMPLATE");

    this.createConnection();
    // setTimeout(() => {
    //   this.isLoaded = true;
    // }, 1500);
  },
};
</script>

<style lang="scss">
.my-error-page {
  width: calc(100vw - 62px);
  min-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 1201px) {
  .my-error-page {
    width: calc(100vw - 62px - 260px);
  }
}
</style>
